import { z } from 'zod';

import type { SituacaoDeContrato } from '../contratos.js';
import { OBRA_PENDENCIAS, OBRA_STATUSES, type ObraStatus, type VisitStatus } from '../domain.js';
import {
  centsSchema,
  csvEnumSchema,
  csvIdsSchema,
  dateOnlySchema,
  idSchema,
  latitudeSchema,
  longitudeSchema,
  optionalText,
  paginationQuerySchema,
  type Paginated,
} from './common.js';

/**
 * Datas `yyyy-mm-dd` são lexicograficamente ordenáveis, então a comparação
 * de string basta — não é preciso converter para `Date`.
 */
interface ObraPeriods {
  readonly contractStart?: string | undefined;
  readonly contractEnd?: string | undefined;
  readonly osStart?: string | undefined;
  readonly osEnd?: string | undefined;
  readonly latitude?: number | undefined;
  readonly longitude?: number | undefined;
}

const contractPeriodIsOrdered = (value: ObraPeriods): boolean =>
  !value.contractStart || !value.contractEnd || value.contractStart <= value.contractEnd;

const osPeriodIsOrdered = (value: ObraPeriods): boolean =>
  !value.osStart || !value.osEnd || value.osStart <= value.osEnd;

/** Coordenada é par: uma sem a outra não localiza nada. */
const coordinateIsComplete = (value: ObraPeriods): boolean =>
  (value.latitude == null) === (value.longitude == null);

/**
 * Campos da obra, **sem valor padrão**.
 *
 * O padrão entra só no schema de criação. Num schema de atualização, um
 * `.default()` transforma "campo não enviado" em "campo enviado com o padrão" —
 * e um PATCH parcial passa a zerar o valor inicial, voltar a situação para
 * "em andamento" e desmarcar reforma, sem que o cliente tenha pedido nada
 * disso. Foi assim que uma obra perdeu contrato, valor e prazos em produção.
 */
const obraFields = {
  name: z.string().trim().min(3, 'Informe a denominação da obra').max(200),
  description: optionalText(2000),
  isReform: z.boolean(),

  contractNumber: optionalText(40),
  companyId: idSchema.optional(),
  responsibleFiscalId: idSchema.optional(),

  status: z.enum(OBRA_STATUSES),
  initialValueCents: centsSchema,

  contractStart: dateOnlySchema.optional(),
  contractEnd: dateOnlySchema.optional(),
  osStart: dateOnlySchema.optional(),
  osEnd: dateOnlySchema.optional(),

  /** Ausente, a obra herda o padrão da organização (15 dias). */
  visitIntervalDays: z.number().int().min(1).max(365).optional(),

  latitude: latitudeSchema.optional(),
  longitude: longitudeSchema.optional(),
  address: optionalText(300),
  city: optionalText(120),
  state: z
    .string()
    .trim()
    .length(2, 'Use a sigla do estado, com 2 letras')
    .toUpperCase()
    .optional(),

  secretariaId: idSchema.optional(),
  coverMediaId: idSchema.optional(),
};

/**
 * O mínimo para uma obra existir.
 *
 * Denominação, número do contrato e valor contratado são o que distingue um
 * registro de obra de uma anotação: sem contrato não há o que fiscalizar, e sem
 * valor o livro-razão nasce sem base de comparação — toda medição posterior
 * seria percentual de zero.
 *
 * A exigência vale só na criação. Na atualização o schema é parcial, e continua
 * possível corrigir um campo de cada vez; e as 19 obras migradas já têm os três
 * preenchidos, conferido antes de apertar a regra.
 */
export const createObraBodySchema = z
  .object({
    ...obraFields,
    contractNumber: z.string().trim().min(1, 'Informe o número do contrato').max(40),
    initialValueCents: centsSchema.refine(
      (valor) => valor > 0,
      'Informe o valor inicial contratado',
    ),
    // Só na criação: aqui "não informado" tem mesmo um padrão razoável.
    isReform: obraFields.isReform.default(false),
    status: obraFields.status.default('em_andamento'),
    /**
     * A equipe que nasce com a obra, além do responsável: entram como
     * auxiliares, como na aba Equipe. Quem cadastra a obra pode incluí-los —
     * é a mesma regra da equipe depois de criada (ADR 045).
     */
    teamMemberIds: z.array(idSchema).max(50, 'No máximo 50 pessoas na equipe').optional(),
  })
  .refine(contractPeriodIsOrdered, {
    message: 'O fim do contrato não pode ser anterior ao início',
    path: ['contractEnd'],
  })
  .refine(osPeriodIsOrdered, {
    message: 'O fim da OS não pode ser anterior ao início',
    path: ['osEnd'],
  })
  .refine(coordinateIsComplete, {
    message: 'Informe latitude e longitude juntas',
    path: ['longitude'],
  });
export type CreateObraBody = z.infer<typeof createObraBodySchema>;

export const updateObraBodySchema = z
  .object(obraFields)
  .partial()
  .refine(contractPeriodIsOrdered, {
    message: 'O fim do contrato não pode ser anterior ao início',
    path: ['contractEnd'],
  })
  .refine(osPeriodIsOrdered, {
    message: 'O fim da OS não pode ser anterior ao início',
    path: ['osEnd'],
  });
export type UpdateObraBody = z.infer<typeof updateObraBodySchema>;

/**
 * Filtros da listagem.
 *
 * Todos são de seleção múltipla e se combinam por E entre dimensões, OU dentro
 * da mesma dimensão: `fiscalIds=a,b&statuses=concluida` lê "obras do fiscal a
 * OU b, que estejam concluídas". As antigas abas fixas (minhas / todas /
 * concluídas) eram três recortes possíveis dessas dimensões — filtro de fiscal
 * e de situação cobrem os três, e mais.
 */
export const listObrasQuerySchema = paginationQuerySchema.extend({
  search: z.string().trim().max(120).optional(),
  statuses: csvEnumSchema(OBRA_STATUSES),
  fiscalIds: csvIdsSchema,
  companyIds: csvIdsSchema,
  /** `nova` é obra nova; `reforma`, obra de reforma. */
  kinds: csvEnumSchema(['nova', 'reforma']),
  /**
   * O que pede ação na obra, apurado como no painel (`pendenciasDaObra`).
   *
   * É o filtro que o painel aplica ao mandar para cá: o contador de contratos
   * vencidos abre a lista com `pendencias=contrato_vencido`, e os dois números
   * batem porque a conta é a mesma.
   */
  pendencias: csvEnumSchema(OBRA_PENDENCIAS),
  /**
   * Ordenação da listagem.
   *
   * `progress` usa a execução financeira (pago sobre o valor atual), que é o
   * "quanto a obra andou" que o domínio reconhece — obra sem medição paga não
   * andou, por mais tempo de contrato que tenha decorrido.
   */
  sort: z.enum(['recent', 'name', 'value', 'next_visit', 'updates', 'progress']).default('recent'),
});
export type ListObrasQuery = z.infer<typeof listObrasQuerySchema>;

/**
 * O recorte para o modo mapa: os mesmos filtros e a mesma ordem da listagem,
 * sem página. O mapa mostra tudo o que os filtros deixam passar — paginar
 * marcadores esconderia obras do mesmo bairro em páginas diferentes.
 */
export const mapObrasQuerySchema = listObrasQuerySchema.omit({ page: true, perPage: true });
export type MapObrasQuery = z.infer<typeof mapObrasQuerySchema>;

/**
 * Retrato do recorte, para a faixa de resumo abaixo dos filtros.
 *
 * A contagem por situação é apurada **ignorando o filtro de situação**: se ela
 * o respeitasse, escolher "concluída" faria a faixa repetir a escolha e zerar
 * todo o resto, justamente quando saber o que há do outro lado é mais útil. Os
 * demais filtros valem, porque a faixa descreve o recorte que está em tela.
 */
export interface ObraListSummary {
  readonly byStatus: Readonly<Record<ObraStatus, number>>;
  /** Obras com relatório publicado desde a última vez que quem olha as abriu. */
  readonly withUpdates: number;
}

export interface ObraListResult extends Paginated<ObraSummary> {
  readonly summary: ObraListSummary;
}

/**
 * A obra no mapa: o resumo do cartão, o ponto — nulo quando a obra não tem
 * localização — e o que o painel lateral do mapa mostra sem abrir a ficha.
 */
export interface ObraMapItem extends ObraSummary {
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly address: string | null;
  readonly city: string | null;
  readonly state: string | null;
  /** A contratada, pelo nome com que é conhecida: o fantasia, senão a razão social. */
  readonly companyName: string | null;
}

/**
 * O recorte inteiro, para o modo mapa.
 *
 * `total` conta o recorte; `items` só vem menor que ele se passar do teto da
 * rota, que um município não alcança. Obra sem coordenada vem junto, com o
 * ponto nulo: a lista ao lado do mapa a mostra, dizendo que falta a
 * localização.
 */
export interface ObraMapResult {
  readonly items: readonly ObraMapItem[];
  readonly total: number;
  readonly summary: ObraListSummary;
}

// ───────────────────────────────────────────────────────── contratos

/**
 * O acompanhamento de contratos: os contratos são as obras, e nada se cadastra
 * por ali.
 *
 * `minhas` são as obras da pessoa — que ela cadastrou, pelas quais responde ou
 * em cuja equipe está, a mesma "obra da pessoa" do alcance de edição (ADR 052);
 * `orgao` é o órgão inteiro, que todo papel já lê na lista de obras. Busca,
 * filtro por situação e ordem correm na tela, sobre o escopo que veio inteiro.
 */
export const listContratosQuerySchema = z.object({
  escopo: z.enum(['minhas', 'orgao']).default('minhas'),
});
export type ListContratosQuery = z.infer<typeof listContratosQuerySchema>;
export type EscopoDeContratos = ListContratosQuery['escopo'];

/** Uma linha da planilha de contratos: o contrato da obra, com a situação já apurada. */
export interface ContratoDaObra {
  readonly obraId: string;
  readonly obraName: string;
  readonly obraStatus: ObraStatus;
  readonly contractNumber: string | null;
  /** A contratada, pelo nome com que é conhecida: o fantasia, senão a razão social. */
  readonly company: {
    readonly id: string;
    readonly name: string;
    readonly cnpj: string | null;
  } | null;
  readonly responsibleFiscal: ObraPerson | null;
  readonly initialValueCents: number;
  /** O valor do contrato hoje, depois de aditivos e supressões. */
  readonly currentValueCents: number;
  readonly paidValueCents: number;
  /** O que falta pagar: valor atual menos pago e glosado. */
  readonly saldoCents: number;
  readonly contractStart: string | null;
  readonly contractEnd: string | null;
  readonly situacao: SituacaoDeContrato;
  /** Dias até o fim da vigência; negativo depois de vencido. Ver `situacaoDoContrato`. */
  readonly diasRestantes: number | null;
  /** Fração da vigência já decorrida, em [0, 1]. */
  readonly vigenciaDecorrida: number;
}

export interface ContratosResult {
  readonly items: readonly ContratoDaObra[];
  /** O hoje do órgão, com que a situação foi apurada. */
  readonly hoje: string;
}

/** Empresa como opção de filtro: traz o CNPJ para a busca alcançá-lo. */
export interface ObraFilterCompany extends ObraCompany {
  readonly cnpj: string | null;
}

/** Opções disponíveis para os filtros, restritas ao que existe no acervo. */
export interface ObraFilterOptions {
  readonly fiscais: readonly ObraPerson[];
  readonly companies: readonly ObraFilterCompany[];
  readonly secretarias: readonly ObraSecretaria[];
}

export const assignFiscalBodySchema = z.object({
  userId: idSchema,
  role: z.enum(['responsavel', 'auxiliar']).default('auxiliar'),
});
export type AssignFiscalBody = z.infer<typeof assignFiscalBodySchema>;

// ─────────────────────────────────────────────────────── respostas

export interface ObraPerson {
  readonly id: string;
  readonly name: string;
  readonly avatarUrl: string | null;
}

export interface ObraCompany {
  readonly id: string;
  readonly legalName: string;
  readonly tradeName: string | null;
}

/**
 * O que a consulta de endereço devolve a partir de uma coordenada.
 *
 * Os três campos que o formulário de obra tem. Qualquer um pode vir nulo: um
 * ponto de estrada vicinal não tem logradouro, e um ponto no mar não tem nada.
 */
export interface ReverseGeocode {
  readonly address: string | null;
  readonly city: string | null;
  readonly state: string | null;
}

/** A secretaria que demandou a obra, na ficha dela. */
export interface ObraSecretaria {
  readonly id: string;
  readonly name: string;
  readonly acronym: string | null;
}

export interface ObraSummary {
  readonly id: string;
  readonly name: string;
  readonly contractNumber: string | null;
  readonly status: ObraStatus;
  readonly isReform: boolean;
  readonly currentValueCents: number;
  readonly paidValueCents: number;
  readonly contractStart: string | null;
  readonly contractEnd: string | null;
  readonly coverUrl: string | null;
  readonly responsibleFiscal: ObraPerson | null;
  readonly visitStatus: VisitStatus;
  readonly nextVisitDueAt: string | null;
  /** Fração de tempo decorrido do contrato, em [0, 1]. */
  readonly timeProgress: number;
  /** Fração paga sobre o valor atual do contrato, em [0, 1]. */
  readonly executionProgress: number;
  /**
   * Relatórios publicados desde a última vez que **este** usuário abriu a obra.
   * Quem nunca abriu vê tudo como novidade; abrir a obra zera a contagem.
   */
  readonly updatesSinceLastView: number;
  /** Data do relatório mais recente, para dizer "há quanto tempo". */
  readonly lastActivityAt: string | null;
}

export interface ObraDetail extends ObraSummary {
  readonly description: string | null;
  /**
   * A capa escolhida, quando existe.
   *
   * `coverUrl` do resumo pode ser a primeira foto do relatório mais recente,
   * herdada para o cartão não ficar vazio. Este campo é `null` nesse caso — é
   * ele que diz se a obra tem capa própria.
   */
  readonly coverMediaId: string | null;
  readonly initialValueCents: number;
  readonly glossValueCents: number;
  readonly osStart: string | null;
  readonly osEnd: string | null;
  readonly visitIntervalDays: number;
  readonly lastVisitAt: string | null;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly address: string | null;
  readonly city: string | null;
  readonly state: string | null;
  readonly company: ObraCompany | null;
  readonly secretaria: ObraSecretaria | null;
  readonly fiscais: readonly (ObraPerson & { role: 'responsavel' | 'auxiliar' })[];
  /**
   * Quem cadastrou a obra. Com o responsável, é quem inclui e retira
   * auxiliares da equipe sem precisar de permissão (ADR 045).
   */
  readonly createdById: string | null;
  /**
   * Se quem está lendo pode editar **esta** obra (ADR 052).
   *
   * Apurado no servidor, com a mesma regra que o `PATCH` aplica: a caixinha
   * "Editar obras" e o alcance dela, padrão ou global. A tela só decide se
   * mostra o botão — a regra depende da obra, e repeti-la no navegador faria
   * o botão aparecer onde o servidor recusa, ou o contrário.
   */
  readonly viewerCanEdit: boolean;
  /**
   * Se quem está lendo corrige os relatórios publicados desta obra — edita ou
   * remove (ADR 060): o fiscal responsável por ela, ou quem tem a caixinha.
   * Também é quem enxerga os removidos da obra.
   */
  readonly viewerCanCorrectReports: boolean;
  readonly concludedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}
